import EventBusy from '@mui/icons-material/EventBusy';
import SearchOff from '@mui/icons-material/SearchOff';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';

type DispatchNoResultsEmptyStateVariant = 'nowhere' | 'other-dates';

interface DispatchNoResultsEmptyStateProps {
  dateLabel?: string;
  onShowAllDates?: () => void;
  term: string;
  variant?: DispatchNoResultsEmptyStateVariant;
}

// Prominent, centered "no results" block for the dispatch smart search. Two variants:
// `nowhere` — the term matches nothing on any date, so nudge the dispatcher toward what they can
// search by; `other-dates` — the term has no matches in the selected range but does on other dates,
// so offer a one-click jump to all dates. Replaces the un-prominent ClearFiltersHint in both cases.
function DispatchNoResultsEmptyState({
  dateLabel,
  onShowAllDates,
  term,
  variant = 'nowhere',
}: DispatchNoResultsEmptyStateProps) {
  switch (variant) {
    case 'nowhere':
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: 2,
            py: 5,
            textAlign: 'center',
          }}
        >
          <SearchOff sx={{ color: 'text.disabled', fontSize: 48 }} />
          <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
            {t('dispatch.smart_search.no_results_title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: 420 }}>
            {t('dispatch.smart_search.no_results_subtitle', { term })}
          </Typography>
        </Box>
      );
    case 'other-dates':
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            px: 2,
            py: 5,
            textAlign: 'center',
          }}
        >
          <EventBusy sx={{ color: 'text.disabled', fontSize: 48 }} />
          <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
            {t('dispatch.smart_search.no_results_on_date_title', { date: dateLabel })}
          </Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: 420 }}>
            {t('dispatch.smart_search.no_results_on_date_subtitle', { term })}
          </Typography>
          <SmallButton
            color="brandV2Green"
            onClick={onShowAllDates}
            sx={{ mt: 1 }}
            variant="contained"
          >
            {t('dispatch.smart_search.show_all_dates_action')}
          </SmallButton>
        </Box>
      );
    default:
      throw new Error(`Unsupported empty-state variant: ${variant}`);
  }
}

export default DispatchNoResultsEmptyState;
